import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import {
  GlobalDataService,
  SessionData,
} from '../global-data/global-data.service';
import {
  BehaviorSubject,
  firstValueFrom,
  map,
  mergeMap,
  Observable,
  Subscription,
} from 'rxjs';
import { MainService } from '../main.service';
import { UserTypeEnum } from '../../types';
import { parseISO } from 'date-fns';
import { Title } from '@angular/platform-browser';

export interface CommentUser {
  id: number;
  displayName: string;
  userType: UserTypeEnum;
}

export interface ThreadDto {
  id: number;
  createdAt: string;
  createdByUser: CommentUser;
  resolvedAt: string | null;
  resolvedByUser: CommentUser | null;
  title: string | null;
  lastCommentAt: string;
  comments: CommentDto[];
  subscriptions: ThreadSubscriptionDto[];
}

export interface CommentDto {
  id: number;
  createdAt: string;
  message: string;
  user: CommentUser;
  threadId: number;
  parentId: number | null;
  metadata: Record<string, string>;
}
export interface ThreadSubscriptionDto {
  userId: number;
  lastSeenAt: string;
  lastNotifiedAt: string;
  isSubscribed: boolean;
  isMuted: boolean;
  isMentioned: boolean;
}

export interface SendoutCommentThread {
  id: number;
  lastCommentAt: Date;
  resolvedAt: Date | null;
  title: string | null;
  hasUnread?: boolean;
  comments: SendoutComment[];
}

export interface SendoutComment {
  id: number;
  user: CommentUser;
  message: string;
  createdAt: Date;
  metadata: Record<string, string>;
}

export interface SendoutCommentOverview {
  sendoutId: number;
  commentCount: number;
  latestCommentAt: Date;
  latestCommentBy: CommentUser;
  refreshesAt: Date;
}

@Injectable({
  providedIn: 'root',
})
export class SendoutCommentsService implements OnDestroy {
  private sessionDataSubscription: Subscription;
  private sessionData?: SessionData;

  private sendoutComments: Map<
    number, // Sendout ID
    BehaviorSubject<SendoutCommentThread[]>
  > = new Map();

  private sendoutCommentOverviews: Map<number, SendoutCommentOverview> =
    new Map();

  constructor(
    private http: HttpClient,
    private globalData: GlobalDataService,
    private mainService: MainService
  ) {
    this.sessionDataSubscription = this.globalData
      .getSessionData()
      .subscribe((sessionData) => {
        this.sessionData = sessionData;
      });
  }

  ngOnDestroy(): void {
    throw new Error('Method not implemented.');
  }

  handleThreadResponse(rawThreads: ThreadDto[]): SendoutCommentThread[] {
    return rawThreads.map((rawThread: ThreadDto) => {
      const comments = rawThread.comments.map((rawComment: CommentDto) => {
        const c: SendoutComment = {
          id: rawComment.id,
          user: rawComment.user,
          message: rawComment.message,
          createdAt: parseISO(rawComment.createdAt),
          metadata: rawComment.metadata,
        };
        return c;
      });

      const sub = rawThread.subscriptions;
      const thread: SendoutCommentThread = {
        id: rawThread.id,
        lastCommentAt: parseISO(rawThread.lastCommentAt),
        resolvedAt: rawThread.resolvedAt
          ? parseISO(rawThread.resolvedAt)
          : null,
        title: rawThread.title,
        comments: comments,
      };
      thread.hasUnread =
        rawThread.subscriptions.find((s) =>
          s.lastSeenAt ? parseISO(s.lastSeenAt) < thread.lastCommentAt : true
        ) != null;
      return thread;
    });
  }

  getCommentThreadsForSendout(
    sendoutId: number
  ): Observable<SendoutCommentThread[]> {
    if (this.sendoutComments.has(sendoutId)) {
      const threads = this.sendoutComments.get(sendoutId);
      if (threads) {
        return threads.asObservable();
      }
    }

    return this.http
      .get<ThreadDto[]>(
        `${this.mainService.getApiURL()}Comment/sendouts/${sendoutId}`,
        {
          headers: this.mainService.getHeaders(),
        }
      )
      .pipe(
        map((threads: ThreadDto[]) => this.handleThreadResponse(threads)),
        mergeMap((threads: SendoutCommentThread[]) => {
          this.sendoutComments.set(
            sendoutId,
            new BehaviorSubject<SendoutCommentThread[]>(threads)
          );
          return this.sendoutComments.get(sendoutId)!.asObservable();
        })
      );
  }

  loadCommentThreadsForSendout(sendoutId: number) {
    return this.http
      .get<ThreadDto[]>(
        `${this.mainService.getApiURL()}Comment/sendouts/${sendoutId}`,
        {
          headers: this.mainService.getHeaders(),
        }
      )
      .pipe(
        map((threads: ThreadDto[]) => this.handleThreadResponse(threads)),
        mergeMap((threads: SendoutCommentThread[]) => {
          this.sendoutComments.get(sendoutId)?.next(threads);
          return threads;
        })
      );
  }

  addCommentForSendout(
    sendoutId: number,
    comment: string,
    threadId?: number,
    parentId?: number,
    metadata?: Record<string, string>
  ) {
    return this.http
      .post<CommentDto>(
        `${this.mainService.getApiURL()}Comment/sendouts/${sendoutId}`,
        {
          threadId: threadId,
          parentId: parentId,
          message: comment,
          metadata: metadata,
        },
        {
          headers: this.mainService.getHeaders(),
        }
      )
      .pipe(
        map(async (c) => {
          await firstValueFrom(this.loadCommentThreadsForSendout(sendoutId));

          return c;
        })
      );
  }

  async markThreadRead(threadId: number) {
    return await firstValueFrom(
      this.http.post(
        `${this.mainService.getApiURL()}Comment/threads/${threadId}`,
        {},
        {
          headers: this.mainService.getHeaders(),
        }
      )
    );
  }
}
